/**
 * Department board (WS-F, workspace docs/guardian/06, 2026-10-23): members share
 * a note or a Guardian answer snapshot with the whole department. Everyone reads
 * the board; an author deletes their own post and an admin can remove any. All
 * post text comes from the Backend and is rendered as text only (bounded: notes
 * <= 8000 chars, the attached comment <= 1000). The Backend does not return an
 * author name, only the author's id, so a post is labelled "You" when it is the
 * caller's own and "A teammate" otherwise (no cross-member identity leak). A 503
 * `org_unavailable` hides the section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  deleteDepartmentBoardPost,
  fetchDepartmentBoard,
  postDepartmentBoardPost,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';
import { formatDeptTime } from '../utils/departmentOrg';

const PAGE = 50;
export const MAX_NOTE_TEXT = 8000;

function Composer({ onPost }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    const err = await onPost(t);
    setBusy(false);
    if (err) { setError(err); return; }
    setText('');
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <TextField
        size="small"
        label="Share a note with your department"
        value={text}
        onChange={(e) => setText(e.target.value)}
        inputProps={{ maxLength: MAX_NOTE_TEXT, 'aria-label': 'Note text' }}
        multiline
        minRows={2}
      />
      <Box>
        <Button variant="contained" onClick={submit} disabled={busy || !text.trim()}>Post note</Button>
      </Box>
    </Box>
  );
}

function PostRow({ post, mine, canDelete, onDelete }) {
  return (
    <Paper variant="outlined" component="li" sx={{ p: 1.5, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Chip size="small" variant="outlined" label={post.kind === 'answer' ? 'Shared answer' : 'Note'} />
          <Typography variant="caption" color="text.secondary">{mine ? 'You' : 'A teammate'}</Typography>
          {formatDeptTime(post.createdAt) && (
            <Typography variant="caption" color="text.secondary">· {formatDeptTime(post.createdAt)}</Typography>
          )}
        </Box>
        {canDelete && <Button size="small" color="error" onClick={() => onDelete(post)}>Delete</Button>}
      </Box>
      {post.kind === 'answer' && post.note && (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{post.note}</Typography>
      )}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{post.text}</Typography>
    </Paper>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {boolean} [props.isAdmin]
 * @param {string|null} [props.currentUserId] - The caller's Guardian user id, for "mine".
 */
export default function DepartmentBoard({ authToken, isAdmin = false, currentUserId = null }) {
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    if (!authToken) return;
    setStatus('loading');
    const res = await fetchDepartmentBoard(authToken, { limit: PAGE });
    if (!alive.current) return;
    if (res?.success) {
      setPosts(Array.isArray(res.data?.posts) ? res.data.posts : []);
      setCursor(res.data?.nextCursor || null);
      setStatus('ready');
    } else if (res?.data?.code === 'org_unavailable') {
      setOff(true);
    } else {
      setStatus('error');
      setError(departmentErrorMessage(res, 'Could not load the board.'));
    }
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  const onPost = async (text) => {
    setError(null);
    const res = await postDepartmentBoardPost(authToken, { kind: 'note', text });
    if (res?.success) { load(); return null; }
    return departmentErrorMessage(res, 'Could not post the note.');
  };

  const onDelete = async (post) => {
    setError(null);
    const res = await deleteDepartmentBoardPost(authToken, post.id);
    if (res?.success) { load(); return; }
    setError(departmentErrorMessage(res, 'Could not delete the post.'));
  };

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    const res = await fetchDepartmentBoard(authToken, { cursor, limit: PAGE });
    setLoadingMore(false);
    if (!alive.current) return;
    if (res?.success) {
      setPosts((prev) => [...prev, ...(Array.isArray(res.data?.posts) ? res.data.posts : [])]);
      setCursor(res.data?.nextCursor || null);
    } else {
      setError(departmentErrorMessage(res, 'Could not load more posts.'));
    }
  };

  if (off) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Department board</Typography>
      <Typography variant="body2" color="text.secondary">
        Notes and Guardian answers your team has shared. Share an answer from the chat with &quot;Share to department&quot;.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Composer onPost={onPost} />

      {status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading the board…</Typography>
        </Box>
      )}

      {status === 'ready' && (
        posts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nothing shared yet.</Typography>
        ) : (
          <>
            <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {posts.map((p) => {
                const mine = Boolean(currentUserId && p.authorGuardianUserId === currentUserId);
                return (
                  <PostRow key={p.id} post={p} mine={mine} canDelete={mine || isAdmin} onDelete={onDelete} />
                );
              })}
            </Box>
            {cursor && (
              <Box>
                <Button size="small" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load older posts'}
                </Button>
              </Box>
            )}
          </>
        )
      )}
    </Paper>
  );
}
